import * as React from 'react'
import { File, FileSpreadsheet, FileText, X } from 'lucide-react'
import type { AnalysisPostAttachment } from '../services/analysisPostAttachmentsService'
import type { CommunityDraftAttachment } from '@/modules/comunidade/hooks/useCommunityDraftAttachments'
import {
  getAttachmentDisplayName,
  getFileExtension,
  isImageAttachment,
} from '@/modules/comunidade/utils/communityAttachments'

type AnalysisPostAttachmentsEditorProps = {
  existingAttachments: AnalysisPostAttachment[]
  onRemoveExisting: (id: number) => void
  draftAttachments: CommunityDraftAttachment[]
  onRemoveDraft: (id: string) => void
}

function getFileIconByExtension(ext: string) {
  if (ext === 'pdf' || ext === 'txt' || ext === 'md' || ext === 'json') return FileText
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return FileSpreadsheet
  return File
}

export function AnalysisPostAttachmentsEditor({
  existingAttachments,
  onRemoveExisting,
  draftAttachments,
  onRemoveDraft,
}: AnalysisPostAttachmentsEditorProps) {
  const existing = Array.isArray(existingAttachments) ? existingAttachments : []
  const draft = Array.isArray(draftAttachments) ? draftAttachments : []
  const hasAny = existing.length > 0 || draft.length > 0
  if (!hasAny) return null

  return (
    <div className="flex flex-wrap gap-2">
      {existing.map((att) => {
        const isImage = isImageAttachment({
          contentType: att.contentType,
          url: att.url,
          storagePath: att.storagePath,
        })

        if (isImage) {
          return (
            <div
              key={`existing-${att.id}`}
              className="relative h-16 w-16 overflow-hidden rounded-md border border-[#2a2a2a] bg-[#111111]"
            >
              <img
                src={att.url}
                alt="Anexo do post (imagem)"
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={() => onRemoveExisting(att.id)}
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"
                aria-label="Remover anexo"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )
        }

        const name = getAttachmentDisplayName({ url: att.url, storagePath: att.storagePath })
        const ext = getFileExtension(name)
        const Icon = getFileIconByExtension(ext)

        return (
          <div
            key={`existing-${att.id}`}
            className="relative flex h-16 max-w-[240px] items-center gap-2 overflow-hidden rounded-md border border-[#2a2a2a] bg-[#111111] px-2 pr-8"
            title={name}
          >
            <Icon className="h-5 w-5 flex-none text-foreground/70" />
            <div className="min-w-0">
              <div className="truncate text-[12px] text-foreground/90">{name}</div>
              <div className="text-[11px] text-foreground/50">
                {ext ? ext.toUpperCase() : 'ARQUIVO'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onRemoveExisting(att.id)}
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"
              aria-label="Remover anexo"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )
      })}

      {draft.map((att) => {
        const ext = getFileExtension(att.file?.name || '')
        const Icon = getFileIconByExtension(ext)
        const isImage = att.kind === 'image'

        return (
          <div
            key={`draft-${att.id}`}
            className={
              isImage
                ? 'relative h-16 w-16 overflow-hidden rounded-md border border-[#2a2a2a] bg-[#111111]'
                : 'relative flex h-16 max-w-[240px] items-center gap-2 overflow-hidden rounded-md border border-[#2a2a2a] bg-[#111111] px-2 pr-8'
            }
          >
            {isImage ? (
              <img
                src={att.previewUrl}
                alt="Pré-visualização do anexo"
                className="h-full w-full object-cover"
              />
            ) : (
              <>
                <Icon className="h-5 w-5 flex-none text-foreground/70" />
                <div className="min-w-0">
                  <div className="truncate text-[12px] text-foreground/90">
                    {att.file?.name || 'arquivo'}
                  </div>
                  <div className="text-[11px] text-foreground/50">
                    {ext ? ext.toUpperCase() : 'ARQUIVO'}
                  </div>
                </div>
              </>
            )}

            <button
              type="button"
              onClick={() => onRemoveDraft(att.id)}
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"
              aria-label="Remover anexo"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )
      })}
    </div>
  )
}

